import {
  AddBtn,
  CardNumber,
  CardStatus,
  CardSubtitle,
  CardTitle,
  ColumnHeader,
  ColumnImgLeft,
  ColumnTitle,
  ColumnWrapper,
  DashboardWrapper,
  Fail,
  FailNumber,
  Success,
  SuccessNumber,
  TaskCardWrapper,
  WorkflowManagementWrapper
} from "./styles";
import { Roles } from "../../models/common/models.enum";
import { ROUTE_PATH } from "../../common/constants/app.constant";
import WidgetsIcon from "@mui/icons-material/Widgets";
import { Box, Button } from "@mui/material";
import { Add, AddRounded, Done, RemoveRounded } from "@mui/icons-material";

import { useCallback, useEffect, useRef, useState } from "react";
import CreateWorkflowDialog from "./components/CreateWorkflowDialog/create_workflow_dialog";
import WorkflowBoard from "./components/WorkflowBoard/WorkflowBoard";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import IconDasboardHeader from "../../assets/images/sidebar/icon_dasboard_header.png";
import useAxios from "../../components/UseAxios/useAxios";
import WorkflowService from "../../services/api/workflow.service";
import { groupBy } from "lodash";
import { IWorkflowByTypeResponse } from "../../models/workflow";
import { toggleMessage } from "../../components/Toast/Toast";
import useNavigateSearch from "../../components/NavigateSearch";
import variableStyles from "../../theme/variable-styles";
import { useTranslation } from "react-i18next";

export interface ITabItem {
  href: string;
  title: string;
  code?: string;
}

enum TabCode {
  AVAILABILITY = "availability",
  CLOSED = "closed",
  MINE = "mine"
}

/**
 * WorkflowManagement Component
 *
 * A React component that manages workflow interface including tabs for different workflow views
 * and creation of new workflows.
 *
 * @component
 *
 * @state {number} tabIndex - Current active tab index
 * @state {boolean} openWorkflowForm - Controls visibility of workflow creation form
 * @state {number} key - Used to force re-render of child components
 *
 * @hooks
 * - useNavigateSearch: Custom hook for navigation with search params
 * - useSearchParams: Hook to access URL search parameters
 * - useCallback: Memoizes the reload function
 * - useEffect: Handles tab selection based on URL parameters
 *
 * @handlers
 * - handleOpenCreateWorkflow: Opens the workflow creation dialog
 * - handleCloseCreateWorkflow: Closes the workflow creation dialog
 * - handleCreateWorkflowSuccess: Handles successful workflow creation by reloading and switching tabs
 *
 * @returns {JSX.Element} Workflow management interface with:
 * - Header with title and create workflow button
 * - Tab navigation
 * - WorkflowBoard components for different views (Available, Closed, Mine)
 * - CreateWorkflowDialog for new workflow creation
 */
const WorkflowManagement = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const tabItems = (role?: Roles): ITabItem[] => {
    const AVAILABIILITY: ITabItem = {
      href: ROUTE_PATH.WORKFLOWS_MANAGEMENT,
      title: t("Management.AvailableWorkflows"),
      code: TabCode.AVAILABILITY
    };
    const CLOSED: ITabItem = {
      href: ROUTE_PATH.WORKFLOWS_MANAGEMENT,
      title: t("Management.ClosedWorkflow"),
      code: TabCode.CLOSED
    };
    const MINE: ITabItem = {
      href: ROUTE_PATH.WORKFLOWS_MANAGEMENT,
      title: t("Management.WorkflowCreatedbyMe"),
      code: TabCode.MINE
    };
    return [AVAILABIILITY, CLOSED, MINE];
  };

  const tabItemsData = tabItems();

  const [tabIndex, setTabIndex] = useState(5);
  const [openWorkflowForm, setOpenWorkflowForm] = useState(false);
  const [key, setKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMyBoard, setIsMyBoard] = useState(false);
  const [isStatus, setIsStatus] = useState(false);
  const [typeBoard, setTypeBoard] = useState<string | undefined>("");

  const [state, setState] = useState<{
    workflows: IWorkflowByTypeResponse[];
  }>({ workflows: [] });

  const navigateSearch = useNavigateSearch();

  const goBoard = (boardId: string) =>
    navigateSearch(ROUTE_PATH.PROJECTS, {
      id: boardId,
      type: "board"
      //tab: tabCode
    });

  const [searchParams] = useSearchParams();

  const reload = useCallback(() => {
    setKey(prevKey => prevKey + 1);
  }, []);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) {
      setTabIndex(tabItemsData.findIndex(item => item.code === tab));
    }
    if (!tab) {
      setTabIndex(0);
    }
  }, [searchParams]);

  const handleOpenCreateWorkflow = (typeBoard: string | undefined) => {
    typeBoard && setTypeBoard(typeBoard);
    setOpenWorkflowForm(true);
  };

  const handleCloseCreateWorkflow = () => {
    setTypeBoard("");
    setOpenWorkflowForm(false);
  };

  const handleCreateWorkflowSuccess = () => {
    getData();
    //setTabIndex(2);
  };

  useEffect(() => {
    getData();
  }, [isMyBoard, isStatus]);

  const getData = async () => {
    try {
      const pageSize = 10000;

      const response = await WorkflowService.getWorkflows({
        currentPage,
        pageSize,
        IsMyBoard: isMyBoard,
        IsStatus: isStatus
      });

      // xử lý dữ liệu khi thành công
      if (response.success && response.items) {
        setState({ workflows: response.items });
      } else {
        toggleMessage({
          type: "error",
          message: response.message ?? ""
        });
      }
    } catch (error) {
      // xử lý lỗi
      console.error("Error:", error);
    }
  };

  // useEffect(() => {
  //   const el = containerRef.current;
  //   if (!el) return;

  //   const handleWheel = (e: WheelEvent) => {
  //     if (e.deltaY === 0) return;
  //     e.preventDefault();
  //     el.scrollLeft += e.deltaY; // 👈 đổi trục Y thành X
  //   };

  //   el.addEventListener("wheel", handleWheel, { passive: false });
  //   return () => {
  //     el.removeEventListener("wheel", handleWheel);
  //   };
  // }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let isDown = false;
    let startX: number;
    let scrollLeft: number;

    const handleMouseDown = (e: MouseEvent) => {
      isDown = true;
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;

      // tắt select text
      document.body.style.userSelect = "none";
      el.style.cursor = "grabbing";
    };

    const handleMouseLeave = () => {
      isDown = false;
      document.body.style.userSelect = "";
      el.style.cursor = "grab";
    };

    const handleMouseUp = () => {
      isDown = false;
      document.body.style.userSelect = "";
      el.style.cursor = "grab";
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const walk = (x - startX) * 1;
      el.scrollLeft = scrollLeft - walk;
    };

    el.addEventListener("mousedown", handleMouseDown);
    el.addEventListener("mouseleave", handleMouseLeave);
    el.addEventListener("mouseup", handleMouseUp);
    el.addEventListener("mousemove", handleMouseMove);

    return () => {
      el.removeEventListener("mousedown", handleMouseDown);
      el.removeEventListener("mouseleave", handleMouseLeave);
      el.removeEventListener("mouseup", handleMouseUp);
      el.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <>
      <WorkflowManagementWrapper spinner>
        <Helmet>
          <meta charSet="utf-8" />
          <title>{t("Management.WorkflowManagement")}</title>
        </Helmet>
        <div className="workflow-header flex">
          <div className="workflow-icon">
            <WidgetsIcon sx={{ color: "#AAAAAA", width: 18, height: 18 }} />
          </div>

          <Box className="workflow-header-info" sx={{ flexGrow: 1 }}>
            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"space-between"}
              className="workflow-title"
              sx={{ flexGrow: 1 }}
            >
              <span
                style={{
                  fontWeight: "bold",
                  fontSize: "20px",
                  textTransform: "uppercase",
                  marginTop: "2px"
                }}
                className="name"
              >
                {t("Management.Workflow")}
              </span>
              <Box display={"flex"} flexDirection={"row"} sx={{ height: 40 }}>
                <Button
                  component="label"
                  size="small"
                  variant="contained"
                  sx={{
                    height: 34,
                    backgroundColor: variableStyles.MainColor,

                    borderRadius: "8px",
                    //marginLeft: 2,
                    fontSize: 14,
                    inlineSize: "max-content"
                  }}
                  onClick={() => handleOpenCreateWorkflow("")}
                  startIcon={<Add />}
                >
                  <label> {t("Management.CreateNewWorkflow")}</label>
                </Button>
              </Box>
            </Box>
            <Box className="workflow-tabs" sx={{ flexGrow: 1 }}>
              <ul className="tab-list">
                {tabItemsData.map((tabItem, i) => (
                  <li
                    key={tabItem.code}
                    onClick={() => {
                      switch (i) {
                        case 0:
                          setIsStatus(false);
                          setIsMyBoard(false);
                          break;
                        case 1:
                          setIsStatus(true);
                          setIsMyBoard(false);
                          break;
                        default:
                          setIsStatus(false);
                          setIsMyBoard(true);
                          break;
                      }

                      navigateSearch(ROUTE_PATH.WORKFLOWS_MANAGEMENT, {
                        tab:
                          i === 0
                            ? TabCode.AVAILABILITY
                            : i === 1
                              ? TabCode.CLOSED
                              : TabCode.MINE
                      });
                    }}
                    className={i === tabIndex ? "active" : ""}
                  >
                    {tabItem.title}
                  </li>
                ))}
              </ul>
            </Box>
          </Box>
        </div>
        <CreateWorkflowDialog
          open={openWorkflowForm}
          handleClose={handleCloseCreateWorkflow}
          handleSuccess={handleCreateWorkflowSuccess}
          typeBoard={typeBoard}
        />
      </WorkflowManagementWrapper>

      <DashboardWrapper ref={containerRef}>
        {state.workflows &&
          state.workflows.map(workflow => (
            <ColumnWrapper>
              <ColumnHeader>
                <ColumnImgLeft src={IconDasboardHeader}></ColumnImgLeft>{" "}
                <ColumnTitle>{workflow.typeBoard}</ColumnTitle>
                <Button
                  sx={{ marginLeft: "auto", color: "#000000" }}
                  onClick={() => handleOpenCreateWorkflow(workflow.typeBoard)}
                >
                  <AddRounded
                    sx={{ width: "18px", height: "18px" }}
                  ></AddRounded>
                </Button>
              </ColumnHeader>
              {workflow.boards &&
                workflow.boards.map(board => (
                  <>
                    <TaskCardWrapper onClick={() => goBoard(board.id)}>
                      <CardTitle>{board.name}</CardTitle>
                      <CardNumber>{board.totalTask}</CardNumber>
                      <CardSubtitle> {t("Management.Task")}</CardSubtitle>
                      <CardStatus sx={{ flexDirection: "column" }}>
                        <Success>
                          <SuccessNumber>
                            {board.totalTaskComplete}
                          </SuccessNumber>{" "}
                          {t("Management.Task")}{" "}
                          {t("Management.Done").toLocaleLowerCase()}
                        </Success>
                        <Fail>
                          <FailNumber>{board.totalTaskFailed}</FailNumber>{" "}
                          {t("Management.Fail")}
                        </Fail>
                      </CardStatus>
                    </TaskCardWrapper>
                  </>
                ))}
            </ColumnWrapper>
          ))}
      </DashboardWrapper>
    </>
  );
};

export default WorkflowManagement;
